import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnDestroy,
	OnInit,
	computed,
	effect,
	inject,
	signal
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { BigFormService, ManifestController, SetNextNode } from '@flexus/core';
import { Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil, tap, skipWhile } from 'rxjs/operators';

export interface DocumentRequirement {
	type: string;
	label: string;
	required: boolean;
	max_file_size_mb?: number;
	allowed_file_types?: string[];
	validation_rules?: Record<string, any>;
}

export interface CompanyConfig {
	id: number;
	name: string;
	logo_url: string;
	logo_alt?: string;
	required_documents: DocumentRequirement[];
	additional_identities?: Array<{ identity_type: string; description: string }>;
	next_node?: string;
	metadata?: Record<string, any>;
	demand_creation: boolean;
	analyse_files_with_ai?: boolean;
	source_urlstring?: string | null;
}

@Component({
	selector: 'flx-company-selection',
	templateUrl: './company-selection.component.html',
	styleUrls: ['./company-selection.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanySelectionComponent implements OnInit, OnDestroy {
	// Dependencies
	private readonly bf = inject(BigFormService);
	private readonly controller = inject<ManifestController<any>>(ManifestController as any);

	private readonly destroy$ = new Subject<void>();

	private isDarkTheme(): boolean {
		// Theme names come from `@flexus/ux` (`blackout` = dark, `midday` = light)
		// Use localStorage because this library component doesn't currently depend on the NgRx theme store.
		try {
			const prefsRaw = typeof window !== 'undefined' ? window.localStorage.getItem('flexusPreferences') : null;
			const prefs = prefsRaw ? JSON.parse(prefsRaw) : null;
			const themeName = prefs?.theme?.name;
			return themeName === 'blackout';
		} catch {
			return false;
		}
	}

	private getThemedLogoUrl(url: string | null | undefined): string | null | undefined {
		if (!url) return url;
		if (!this.isDarkTheme()) return url;

		// Makro and OmniKitchen need dark variants for blackout theme
		// Preserve any query/hash suffix.
		return url
			.replace(/makro\.svg(\?.*)?(#.*)?$/i, (_match, query = '', hash = '') => `makro-dark.svg${query}${hash}`)
			.replace(/omnikitchen\.svg(\?.*)?(#.*)?$/i, (_match, query = '', hash = '') => `omnikitchen-dark.svg${query}${hash}`);
	}

	// Signals
	readonly selectedCompanyId = signal<number | null>(null);
	readonly companies = signal<CompanyConfig[]>([]);
	readonly isLoading = signal<boolean>(false);

	// Only expose companies that participate in demand creation to the UI
	// Use demand_creation if set, otherwise fallback to checking if required_documents exist
	readonly visibleCompanies = computed<CompanyConfig[]>(() => {
		const allCompanies = this.companies();
		const filtered = allCompanies.filter(c => {
			// If demand_creation is explicitly set, use it
			if (c.demand_creation !== undefined) {
				return c.demand_creation;
			}
			// Fallback: show if required_documents exist and has items
			return Array.isArray(c.required_documents) && c.required_documents.length > 0;
		});
		console.log('CompanySelectionComponent: visibleCompanies computed - all:', allCompanies.length, 'visible:', filtered.length);
		return filtered;
	});

	readonly selectedCompany = computed<CompanyConfig | null>(() => {
		const id = this.selectedCompanyId();
		if (!id) return null;
		return this.visibleCompanies().find(c => c.id === id) ?? null;
	});

	readonly gridColumns = computed<number>(() => {
		const count = this.visibleCompanies().length;
		if (count <= 0) return 1;
		if (count < 4) return count;
		return 4;
	});

	// Inputs
	@Input() clients$: Observable<CompanyConfig[]> | null = null;
	@Input() clients: CompanyConfig[] | null = null;
	@Input() title = 'Select the company';
	@Input() instructions: string[] = ['Which company is the job for?'];
	@Input() nextNode = 'SupportingDocumentUpload';
	@Input() formControlName = 'selected_company';

	// Effect: keep form control value in sync with selectedCompanyId signal
	private readonly selectionEffect = effect(() => {
		const id = this.selectedCompanyId();
		const control = this.bf.bigForm.get(this.formControlName);
		if (control && control.value !== id) {
			control.setValue(id, { emitEvent: false });
		}
	});

	ngOnInit(): void {
		this.initialiseFormControls();

		// Prefer mock array input if provided
		if (this.clients && this.clients.length) {
			const validated = this.validateCompanyConfigs(this.clients);
			this.companies.set(validated);
			return;
		}

		// Fallback to observable input (future server-driven mode)
		if (this.clients$) {
			this.loadCompanies();
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private initialiseFormControls(): void {
		// Selected company id
		if (!this.bf.bigForm.get(this.formControlName)) {
			this.bf.bigForm.addControl(this.formControlName, new UntypedFormControl(null));
		}

		// Selected company name & logo (optional but useful)
		if (!this.bf.bigForm.get('selected_company_name')) {
			this.bf.bigForm.addControl('selected_company_name', new UntypedFormControl(null));
		}
		if (!this.bf.bigForm.get('selected_company_logo')) {
			this.bf.bigForm.addControl('selected_company_logo', new UntypedFormControl(null));
		}

		// Document configuration for next node
		if (!this.bf.bigForm.get('tenant_document_config')) {
			this.bf.bigForm.addControl('tenant_document_config', new UntypedFormControl(null));
		}

		// Keep signal in sync if form value is already present
		const control = this.bf.bigForm.get(this.formControlName);
		if (control) {
			const current = control.value as number | null;
			if (current) {
				this.selectedCompanyId.set(current);
			}

			control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
				if (value !== this.selectedCompanyId()) {
					this.selectedCompanyId.set(value);
				}
			});
		}
	}

	private loadCompanies(): void {
		if (!this.clients$) {
			console.warn('CompanySelectionComponent: clients$ is null/undefined');
			return;
		}

		this.isLoading.set(true);

		this.clients$
			.pipe(
				tap(data => console.log('CompanySelectionComponent: received data from Observable:', data)),
				skipWhile((companies: CompanyConfig[] | null | undefined) => {
					// Skip until we have an actual array (not null/undefined)
					return !Array.isArray(companies);
				}),
				filter((companies: CompanyConfig[] | null | undefined) => {
					const isArray = Array.isArray(companies);
					if (!isArray) {
						console.warn('CompanySelectionComponent: data is not an array:', companies);
					}
					return isArray;
				}),
				map(companies => {
					console.log('CompanySelectionComponent: before validation, count:', companies.length);
					const validated = this.validateCompanyConfigs(companies as CompanyConfig[]);
					console.log('CompanySelectionComponent: after validation, count:', validated.length);
					return validated;
				}),
				take(1),
				takeUntil(this.destroy$)
			)
			.subscribe({
				next: companies => {
					console.log('CompanySelectionComponent: setting companies, count:', companies.length);
					console.log('CompanySelectionComponent: companies with demand_creation:', companies.filter(c => c.demand_creation).length);
					this.companies.set(companies);
					this.isLoading.set(false);
				},
				error: (err) => {
					console.error('CompanySelectionComponent: error loading companies:', err);
					this.isLoading.set(false);
				}
			});
	}

	private validateCompanyConfigs(companies: CompanyConfig[]): CompanyConfig[] {
		return (companies ?? [])
			.filter(company => !!company && company.id != null && !!company.name)
			.map(company => {
				const required_documents = this.validateDocumentRequirements(company.required_documents || []);
				return {
					...company,
					id: Number(company.id),
					name: String(company.name),
					logo_url: String(this.getThemedLogoUrl(company.logo_url) ?? ''),
					logo_alt: company.logo_alt ?? company.name,
					required_documents,
					analyse_files_with_ai: company.analyse_files_with_ai === true,
					source_urlstring: this.getThemedLogoUrl(company.source_urlstring)
				};
			});
	}

	private validateDocumentRequirements(docs: DocumentRequirement[]): DocumentRequirement[] {
		return (docs ?? [])
			.filter(doc => !!doc && !!doc.type && !!doc.label)
			.map(doc => ({
				type: String(doc.type),
				label: String(doc.label),
				required: !!doc.required,
				max_file_size_mb: doc.max_file_size_mb != null ? Number(doc.max_file_size_mb) : undefined,
				allowed_file_types: Array.isArray(doc.allowed_file_types)
					? doc.allowed_file_types.map(t => String(t))
					: undefined,
				validation_rules: doc.validation_rules ?? {}
			}));
	}

	onCompanySelect(company: CompanyConfig): void {
		if (!company) return;

		this.selectedCompanyId.set(company.id);
		this.storeCompanySelection(company);
	}

	onImageError(event: Event): void {
		const target = event?.target as HTMLElement | null;
		if (target) {
			target.style.display = 'none';
		}
	}

	private storeCompanySelection(company: CompanyConfig): void {
		// Persist selection into the form
		this.bf.bigForm.patchValue(
			{
				[this.formControlName]: company.id,
				selected_company_name: company.name,
				selected_company_logo: company.logo_url
			},
			{ emitEvent: false }
		);

		const documentConfig = {
			company_id: company.id,
			company_name: company.name,
			required_documents: company.required_documents ?? [],
			metadata: {
				...(company.metadata ?? {}),
				additional_identities: company.additional_identities ?? []
			},
			demand_creation: company.demand_creation === true,
			analyse_files_with_ai: company.analyse_files_with_ai === true
		};

		this.bf.bigForm.get('tenant_document_config')?.setValue(documentConfig);

		// Only navigate into the claim creation flow when enabled for this client
		if (company.demand_creation) {
			const targetNode = company.next_node || this.nextNode;
			if (targetNode) {
				this.controller.dispatch(new SetNextNode(targetNode));
			}
		}
	}

	isSelected(companyId: number): boolean {
		return this.selectedCompanyId() === companyId;
	}

	getGridTemplateColumns(): string {
		const cols = this.gridColumns();
		// Use fixed track width so that when there are fewer than 4 items,
		// the grid can be horizontally centered with a consistent gap.
		const trackWidthPx = 176;
		return `repeat(${cols}, ${trackWidthPx}px)`;
	}
}


